"use strict";

const BaseModuleSettings = require("./baseModuleSettings");

const LidModuleSettings = function () {
  const tool = "lid";
  const key = "lid-module";
  const formKey = "lid-module-settings";
  const option = "Post-Construction Module";
  const iconClass = "lid";
  const userVariableValidatorClassLidNotice = "validate-lid-notice-variables";
  const userVariableValidatorClassLidSummary = "validate-lid-summary-variables";
  const moduleProps = {
    userVariableValidatorClassLidNotice,
    userVariableValidatorClassLidSummary,
    cycleLengthUnitOptions: [
      { name: "Day", value: "day" },
      { name: "Month", value: "month" },
      { name: "Year", value: "year" },
    ],
  };
  const workflowTypeByCycleGroupKey = {
    maintenanceNotice: "lid-maintenance-notice",
    inspection: "lid-inspection",
  };

  const initValidatorFunction = (props) => {
    UserVariableValidator.init(
      Object.keys(props?.construction?.lidNoticeUserTemplate || {}),
      `.${userVariableValidatorClassLidNotice}`,
    );
    UserVariableValidator.init(
      Object.keys(props?.construction?.lidSummaryUserTemplate || {}),
      `.${userVariableValidatorClassLidSummary}`,
    );
  };

  // Cycle Settings (SLG-4703) is backed by its own API/table, independent of
  // ConstructionSettings, but shares this page's single Save/Cancel buttons and
  // form - see BaseModuleSettings' extraData hook.
  //
  // Gated on the same flag as the template's cycle section: the endpoint is restricted
  // to 2N admins, so fetching it for everyone would fail this whole page's getProps()
  // for customers and stop the settings modal from opening at all.
  const loadCycleProps = async () => {
    if (!FeatureFlag.enabled("lid-todo-cycles")) {
      return null;
    }

    return await ApiCalls.getCycleSettings();
  };

  const saveCycleData = async (cycleFormData) => {
    const cycles = {};
    for (const [groupKey, workflowType] of Object.entries(workflowTypeByCycleGroupKey)) {
      if (cycleFormData[groupKey]) {
        cycles[workflowType] = cycleFormData[groupKey];
      }
    }

    // The date pickers captured the picked dates using the browser's own timezone
    // (they were populated from the group's local digits - see lidModule.njk), so
    // they need to be re-interpreted as the group's local timezone before saving.
    for (const cycle of Object.values(cycles)) {
      for (const dateField of ["cycleStartDate", "cycleDueDate"]) {
        if (cycle[dateField]) {
          cycle[dateField] = DateTime.getIsoString(
            DateTime.getLocalAtGroupTimeZone(DateTime.parseIsoDate(cycle[dateField])),
          );
        }
      }
    }

    await ApiCalls.setCycleSettings({ cycles });
  };

  // Mirrors CycleProvider::shiftByCycles' addMonthsNoOverflow/addYearsNoOverflow, which
  // clamps to the end of the target month: a 1/31 start shifted one month lands on 2/28,
  // where setMonth() would roll over to 3/3 and preview a date the API never stores.
  const addCycleLength = (date, lengthValue, lengthUnit) => {
    if (lengthUnit === "day") {
      const shiftedDate = new Date(date);
      shiftedDate.setDate(shiftedDate.getDate() + lengthValue);

      return shiftedDate;
    }

    const monthsToAdd = lengthUnit === "month" ? lengthValue : lengthValue * 12;
    const targetMonth = new Date(date.getFullYear(), date.getMonth() + monthsToAdd, 1);
    const lastDayOfTargetMonth = new Date(
      targetMonth.getFullYear(),
      targetMonth.getMonth() + 1,
      0,
    ).getDate();

    return new Date(
      targetMonth.getFullYear(),
      targetMonth.getMonth(),
      Math.min(date.getDate(), lastDayOfTargetMonth),
      date.getHours(),
      date.getMinutes(),
      date.getSeconds(),
    );
  };

  // Mirrors CycleUpdateRequest's "due date must fall within the cycle window" rule, so an
  // out-of-window date is caught while the user types rather than by a 422 after they save.
  // Compared by calendar day because both dates are day-only pickers.
  const isDueDateInCycle = (dueDate, startDate, endDate) =>
    DateTime.getDaysDifferenceNumber(dueDate, startDate) >= 0 &&
    DateTime.getDaysDifferenceNumber(endDate, dueDate) >= 0;

  const cycleIsValidByGroupKey = {};

  const setDueDateValidity = ($fieldset, groupKey, isValidCycle) => {
    cycleIsValidByGroupKey[groupKey] = isValidCycle;

    $fieldset
      .find(".cycle-due-date-warning")
      .text(isValidCycle ? "" : "The due date must fall within the cycle window.")
      .toggleClass("default-hidden", isValidCycle);
  };

  // Live preview of the currently-typed (not-yet-saved) Start Date/Length/Due Date,
  // read straight from the DOM rather than Form's diff-only state, so it reflects
  // both untouched initial values and in-progress edits.
  const updateNewCyclePreview = ($page, groupKey) => {
    const startFieldName = `cycles[${groupKey}][cycleStartDate]`;
    const dueFieldName = `cycles[${groupKey}][cycleDueDate]`;
    const startDate = DateTimePicker.getDate($page, startFieldName);
    const dueDate = DateTimePicker.getDate($page, dueFieldName);
    const lengthValue = Number(
      $page.find(`input[name="cycles[${groupKey}][cycleLengthValue]"]`).val(),
    );
    const lengthUnit = $page.find(`select[name="cycles[${groupKey}][cycleLengthUnit]"]`).val();
    const $fieldset = $page.find(`.cycle-fieldset[data-cycle-group="${groupKey}"]`);

    if (!startDate || !lengthValue || !lengthUnit) {
      // No window to measure the due date against, so this rule can't fail yet - the Form's
      // own required-field handling is what should block an incomplete cycle.
      setDueDateValidity($fieldset, groupKey, true);
      return;
    }

    const endDate = addCycleLength(startDate, lengthValue, lengthUnit);
    endDate.setDate(endDate.getDate() - 1);

    $fieldset.find(".new-cycle-start").text(DateTimePicker.getDisplayDate($page, startFieldName));
    $fieldset.find(".new-cycle-end").text(DateTimePicker.formatDateTime(endDate, "m/d/Y"));
    $fieldset.find(".new-cycle-due").text(DateTimePicker.getDisplayDate($page, dueFieldName));

    setDueDateValidity(
      $fieldset,
      groupKey,
      !dueDate || isDueDateInCycle(dueDate, startDate, endDate),
    );
  };

  const isCycleFormValid = () => !Object.values(cycleIsValidByGroupKey).includes(false);

  const onPageReady = ($page) => {
    for (const groupKey of Object.keys(workflowTypeByCycleGroupKey)) {
      updateNewCyclePreview($page, groupKey);
    }

    $page.off("2N:FormInput.cyclePreview");
    $page.on("2N:FormInput.cyclePreview", (event, value, pathParts) => {
      if (pathParts[0] === "cycles") {
        updateNewCyclePreview($page, pathParts[1]);
      }
    });
  };

  const base = new BaseModuleSettings(
    tool,
    key,
    formKey,
    option,
    iconClass,
    moduleProps,
    initValidatorFunction,
    {
      key: "cycles",
      name: "the To Do Cycle",
      loadProps: loadCycleProps,
      save: saveCycleData,
      onPageReady,
      isValid: isCycleFormValid,
    },
  );

  return {
    ...base,
    _saveCycleData: saveCycleData,
    _updateNewCyclePreview: updateNewCyclePreview,
    _isCycleFormValid: isCycleFormValid,
  };
};

module.exports = LidModuleSettings();

const ApiCalls = require("../../apiCalls");
const DateTime = require("../../dateTime");
const DateTimePicker = require("../../general/dateTimePicker");
const FeatureFlag = require("../featureFlag");
const UserVariableValidator = require("../../misc/userVariableValidator");
